import PocketBase from 'pocketbase';
import { JoinRequest, SignalingProvider } from "./SignalingProvider";
import { RecordModel } from "pocketbase";
import SimplePeer from "simple-peer";

/**
 * PocketBase implementation of the SignalingProvider interface
 * Uses PocketBase collections for rooms and join requests
 */
export class PocketBaseProvider extends SignalingProvider {

	constructor(private readonly pocketbase: PocketBase) {
		super();
	}

	/**
	 * Get the current user's ID from PocketBase
	 */
	get userId(): string {
		const id = this.pocketbase.authStore.record?.id;
		if (!id) {
			throw new Error("Not authenticated with PocketBase");
		}
		return id;
	}

	/**
	 * Create a new room in PocketBase
	 * @returns Room ID for the created room
	 */
	async createRoom(): Promise<string> {
		const room = await this.pocketbase.collection('rooms').create({
			host: this.userId,
		});
		return room.id;
	}

	/**
	 * Clean up any previous rooms owned by the current user
	 */
	async cleanupPreviousRooms(): Promise<void> {
		const previousRooms = await this.pocketbase.collection('rooms').getFullList({
			filter: `host="${this.userId}"`,
		});

		if (previousRooms.length > 0) {
			const batch = this.pocketbase.createBatch();
			for (const room of previousRooms) {
				batch.collection('rooms').delete(room.id);
			}
			await batch.send();
		}
	}

	/**
	 * Create a join request for a room in PocketBase
	 * @param roomId ID of the room to join
	 * @returns The created join request
	 */
	async createJoinRequest(roomId: string): Promise<JoinRequest> {
		const joinRequest = await this.pocketbase.collection('join_requests').create({
			room: roomId,
			requestee: this.userId,
			step: 'request',
		});

		return this.mapRecordToJoinRequest(joinRequest);
	}

	/**
	 * Update a join request with an offer in PocketBase
	 * @param requestId ID of the join request to update
	 * @param offer WebRTC offer signal data
	 */
	async updateRequestWithOffer(requestId: string, offer: SimplePeer.SignalData): Promise<void> {
		await this.pocketbase.collection('join_requests').update(requestId, {
			offer,
			step: 'offer',
		});
	}

	/**
	 * Update a join request with an answer in PocketBase
	 * @param requestId ID of the join request to update
	 * @param answer WebRTC answer signal data
	 */
	async updateRequestWithAnswer(requestId: string, answer: SimplePeer.SignalData): Promise<void> {
		await this.pocketbase.collection('join_requests').update(requestId, {
			answer,
			step: 'answer',
		});
	}

	/**
	 * Subscribe to join requests for a room in PocketBase
	 * @param roomId ID of the room to subscribe to
	 * @returns Cleanup function to unsubscribe
	 */
	async subscribeToJoinRequests(roomId: string): Promise<() => Promise<void>> {
		return this.pocketbase
			.collection('join_requests')
			.subscribe('*', (snapshot) => {
				if (snapshot.action === 'delete') return;

				const record = snapshot.record;
				const joinRequest = this.mapRecordToJoinRequest(record);

				switch (record.step) {
					case 'request':
						this.emit('joinRequest', joinRequest);
						break;
					case 'offer':
						this.emit('offerUpdate', joinRequest);
						break;
					case 'answer':
						this.emit('answerUpdate', joinRequest);
						break;
				}
			}, { filter: `room="${roomId}"` });
	}

	/**
	 * Subscribe to updates for a specific join request in PocketBase
	 * @param requestId ID of the join request to subscribe to
	 * @returns Cleanup function to unsubscribe
	 */
	async subscribeToJoinRequestUpdates(requestId: string): Promise<() => Promise<void>> {
		return this.pocketbase
			.collection('join_requests')
			.subscribe(
				requestId,
				(snapshot) => {
					if (snapshot.action === 'delete') {
						return;
					}

					const record = snapshot.record;
					const joinRequest = this.mapRecordToJoinRequest(record);

					switch (record.step) {
						case 'request':
							// No action needed
							break;
						case 'offer':
							this.emit('offerUpdate', joinRequest);
							break;
						case 'answer':
							this.emit('answerUpdate', joinRequest);
							break;
					}
				}
			);
	}

	/**
	 * Clean up resources used by this provider
	 */
	async cleanup(): Promise<void> {
		// No specific cleanup needed for PocketBase
		// The subscriptions are handled by the connection manager
	}

	/**
	 * Verify if a room exists in PocketBase
	 * @param roomId ID of the room to check
	 * @returns Whether the room exists
	 */
	async roomExists(roomId: string): Promise<boolean> {
		try {
			await this.pocketbase.collection('rooms').getOne(roomId);
			return true;
		} catch {
			return false;
		}
	}

	/**
	 * Delete a join request from PocketBase
	 * @param requestId ID of the request to delete
	 */
	async deleteJoinRequest(requestId: string): Promise<void> {
		await this.pocketbase.collection('join_requests').delete(requestId);
	}

	/**
	 * Map a PocketBase record to a JoinRequest object
	 * @param record PocketBase record
	 * @returns JoinRequest object
	 */
	private mapRecordToJoinRequest(record: RecordModel): JoinRequest {
		return {
			id: record.id,
			created: record.created,
			updated: record.updated,
			requestee: record.requestee,
			room: record.room,
			offer: record.offer,
			answer: record.answer,
			step: record.step,
		};
	}
}